import { ValidationResult } from './validation-result.model';
import { Card } from '../models/card.model';
import { CardStatus } from '../constants/app.constants';
import { validateDeadlineString } from './deadline.validator';
import { validateIsoDateTimeString, validateTimestampOrder } from './timestamp.validator';

export function validateCard(card: Card, validCategoryIds: string[], ladderLevels: number[]): ValidationResult {
    const errors: string[] = [];

    if (typeof card.id !== 'string' || card.id.trim() === '') {
        errors.push('Card ID must be a non-empty string.');
    }

    if (typeof card.title !== 'string' || card.title.trim() === '') {
        errors.push('Card title cannot be empty.');
    }

    if (typeof card.description !== 'string') {
        errors.push('Card description must be a string.');
    }

    if (typeof card.notes !== 'string') {
        errors.push('Card notes must be a string.');
    }

    if (typeof card.categoryId !== 'string' || card.categoryId.trim() === '') {
        errors.push('Card category reference is invalid.');
    }

    if (!validCategoryIds.includes(card.categoryId)) {
        errors.push('Card belongs to an invalid category.');
    }

    if (!Object.values(CardStatus).includes(card.status)) {
        errors.push('Card status is invalid.');
    }

    if (card.manualProgress < 0 || card.manualProgress > 100 || !Number.isInteger(card.manualProgress)) {
        errors.push('Card manual progress must be an integer between 0 and 100.');
    }

    if (typeof card.showInLadder !== 'boolean') {
        errors.push('Card ladder visibility flag is invalid.');
    }

    if (card.parentCardId !== null && typeof card.parentCardId !== 'string') {
        errors.push('Card parent reference must be a string or null.');
    }

    const createdAtValidation = validateIsoDateTimeString(card.createdAt, 'Card creation date');
    const updatedAtValidation = validateIsoDateTimeString(card.updatedAt, 'Card update date');
    const timestampOrderValidation = validateTimestampOrder(
        card.createdAt,
        card.updatedAt,
        'Card creation date',
        'Card update date',
    );
    errors.push(...createdAtValidation.errors);
    errors.push(...updatedAtValidation.errors);
    errors.push(...timestampOrderValidation.errors);

    const deadlineResult = validateDeadlineString(
        card.deadline,
        createdAtValidation.isValid ? card.createdAt : null,
    );
    if (!deadlineResult.isValid) {
        errors.push(...deadlineResult.errors);
    }

    if (
        card.ladderGroupColor !== undefined
        && card.ladderGroupColor !== null
        && !/^#[0-9A-Fa-f]{6}$/.test(card.ladderGroupColor)
    ) {
        errors.push('Card ladder group color must be a valid hex color.');
    }

    if (card.showInLadder) {
        if (card.priority === null || card.importance === null) {
            errors.push('Priority and importance must not be null when card is in ladder.');
        } else {
            if (!ladderLevels.includes(card.priority)) {
                errors.push('Card priority must reference an existing ladder level.');
            }
            if (typeof card.importance !== 'number' || !Number.isInteger(card.importance) || card.importance < 1) {
                errors.push('Card importance must be an integer greater than or equal to 1.');
            }
        }
    } else {
        if (card.priority !== null || card.importance !== null) {
            errors.push('Priority and importance must be null when card is hidden from ladder.');
        }
    }

    return { isValid: errors.length === 0, errors };
}
